import React, {useEffect} from 'react';
import {Button} from "../../fusion-ui";
import {useTheme} from "../../ProjectThemeProvider";
import {checkNullStr, getDefStr, isBoolTrue} from "../../fusion-ui/utils/ReactUtils";
import {CssVariant} from "../../fusion-ui/config/FusionConfig";
import {Link} from "react-router-dom";
import useScreenSize from "../../fusion-ui/hook/useScreenSize";

/**
 * Create a theme based button component.
 *
 * @example
 *     <ThemedButton
 *            className={''}
 *            width={''}
 *            height={''}
 *            text={'Test Button'}
 *            tooltip={'Test Button'}
 *            outlined={false}
 *            bgColor={'blue'}
 *            color={'white'}
 *            hoverColor={'white'}
 *            hoverBgColor={'green'}
 *            borderColor={''}
 *            hoverBorderColor={''}
 *            rippleColor={'white15'}
 *            icon={'fa fa-heart'}
 *            iconAsImage={false}
 *            iconSize={'14px'}
 *            asLoading={false}
 *            loadingVariant={'light'}
 *            loadingWidth={'100%'}
 *            textOnLoading={true}
 *            disabled={false}
 *            sleek={false}
 *            href={'url'}
 *            style={{}}
 *            onClick={handleClick}
 *            {...rest}
 *     />
 *
 *     //Short implementation - bg-color
 *      const theme = useTheme();
 *      <ThemedButton
 *          className={''}
 *          mx={2}
 *          variant={theme.primary}
 *          variant={theme.primaryLight}
 *          sleek={true}
 *          text={'Button'}
 *       />
 *
 *     //Short implementation - outlined
 *      const theme = useTheme();
 *      <ThemedButton
 *          className={''}
 *          mx={2}
 *          variant={theme.primary}
 *          sleek={true}
 *          outlined={true}
 *          text={'Outline Button'}
 *       />
 *
 */
/**
 * ThemedButton is a memoized functional component that creates a button with customizable themes and styles.
 *
 * @param {string} wrapperClassName - Additional classes for button wrapper.
 * @param {string} className - Additional classes for the button.
 * @param {string|number} width - Width of the button.
 * @param {string|number} height - Height of the button.
 * @param {string} text - Text to be displayed on the button.
 * @param {string} tooltip - Tooltip text.
 * @param {string} icon - Icon class or image URL for the button.
 * @param {boolean} iconAsImage - If true, treats icon as an image URL.
 * @param {string|number} iconSize - Size of the icon.
 * @param {boolean} asLoading - If true, display loading variant.
 * @param {string} loadingVariant - Variation of the loading animation.
 * @param {string|number} loadingWidth - Width of the loader.
 * @param {boolean} textOnLoading - If true, shows text during loading.
 * @param {boolean} rounded - If true, button corners are rounded.
 * @param {boolean} outlined - If true, button has outline styling.
 * @param {boolean} asLink - If true, button behaves as a link.
 * @param {boolean} enableFocusStyling - If true, styling on focus is enabled.
 * @param {boolean} disableRipple - If true, ripple effect is disabled.
 * @param {boolean} elastic - If true, enables elastic effect.
 * @param {boolean} elasticActive - If true, activates elastic effect.
 * @param {boolean} disabled - If true, disables the button.
 * @param {string} disableVariant - Variation of the disabled button.
 * @param {string|number} borderWidth - Width of the button border.
 * @param {string} shadowVariant - Variation of the shadow.
 * @param {boolean} sleek - If true, button has sleek design.
 * @param {boolean} circular - If true, button is circular.
 * @param {string} target - Target for the link.
 * @param {string} href - URL for the link button.
 * @param {string} bgColor - Background color of the button.
 * @param {string} hoverBgColor - Background color on hover.
 * @param {string} borderColor - Border color of the button.
 * @param {string} hoverBorderColor - Border color on hover.
 * @param {string} color - Text color of the button.
 * @param {string} hoverColor - Text color on hover.
 * @param {string} rippleColor - Color of the ripple effect.
 * @param {string} variant - Variant for the button theme.
 * @param {string} variantLight - Light variant for the button theme.
 * @param {string|number} mx - Horizontal margin.
 * @param {string|number} ml - Margin left.
 * @param {string|number} mr - Margin right.
 * @param {string|number} my - Vertical margin.
 * @param {string|number} mt - Margin top.
 * @param {string|number} mb - Margin bottom.
 * @param {string|number} m - Margin.
 * @param {object} style - Additional styles for the button.
 * @param {object} hoverStyle - Additional styles on hover.
 * @param {object} pressStyle - Additional styles on press.
 * @param {object} touchStyle - Additional styles on touch.
 * @param {function} onClick - Click event handler.
 * @param {function} onLinkClick - Link click event handler.
 * @param {object} rest - Other additional props.
 *
 * @returns {JSX.Element} A themed button component.
 */
const ThemedButton = React.memo(({
                                     wrapperClassName = "",
                                     className = "",
                                     width,
                                     height,
                                     text,
                                     tooltip,
                                     icon,
                                     iconAsImage,
                                     iconSize,
                                     asLoading,
                                     loadingVariant = CssVariant.light,
                                     loadingWidth = 'auto',
                                     textOnLoading = true,
                                     rounded = true,
                                     outlined = false,
                                     asLink = true,
                                     enableFocusStyling = false,
                                     disableRipple = false,
                                     elastic = false,
                                     elasticActive = false,
                                     disabled,
                                     disableVariant = "sm",
                                     borderWidth = '1px',
                                     shadowVariant = 'none',
                                     sleek,
                                     circular,
                                     target,
                                     href,

                                     bgColor,
                                     hoverBgColor,
                                     borderColor,
                                     hoverBorderColor,
                                     color,
                                     hoverColor,
                                     rippleColor,
                                     variant,
                                     variantLight,

                                     mx,
                                     ml,
                                     mr,
                                     my,
                                     mt,
                                     mb,
                                     m,

                                     style = {},
                                     hoverStyle = {},
                                     pressStyle = {},
                                     touchStyle = {},
                                     onClick,
                                     onLinkClick,
                                     ...rest
                                 }) => {
    const TAG = "ThemedButton";

    const theme = useTheme();
    const {isScreenUpSm, isScreenXs, isScreenSm} = useScreenSize();

    useEffect(() => {

    }, []);

    return ((isBoolTrue(asLink) && checkNullStr(href)) ? createLinkButton() : createButton())

    // return (createButton())

    function createLinkButton() {
        return <Link to={href}
                     className={`m-0 p-0 mx-${mx} ms-${ml} me-${mr} my-${my} mt-${mt} mb-${mb} m-${m} ${isBoolTrue(disabled) ? `disable-item-${disableVariant}` : 'cursor-pointer'} ${wrapperClassName}`}
                     target={target}
                     referrerPolicy={'no-referrer'}
                     onClick={onLinkClick}
            // onClick={onClick}
        >
            {createButton()}
        </Link>
    }

    function createButton() {
        return <Button
            className={`w-100 px-2  
            ${isBoolTrue(rounded) ? 'rounded-2' : ''}
            ${isBoolTrue(outlined) ? `border-solid border-1x bg-tx blur-5x` : ""}
            ${(isBoolTrue(asLink) && checkNullStr(href)) ? `m-0 mt-0 mb-0 my-0 mx-0 ms-0 me-0` : `mx-${mx} ms-${ml} me-${mr} my-${my} mt-${mt} mb-${mb} m-${m}`}
            risen-${shadowVariant}
            ${className}`}
            text={text}
            tooltip={tooltip}
            variant={''}
            transitionVariant={'sm'}
            sleek={sleek}
            width={width}
            height={height}
            href={!((isBoolTrue(asLink) && checkNullStr(href))) ? href : null}
            circular={circular}
            autoDisableContextMenu={!checkNullStr(href) && checkNullStr(tooltip)}
            rounded={false}
            icon={isBoolTrue(iconAsImage) ? icon : null}
            iconClass={!isBoolTrue(iconAsImage) ? icon : null}
            iconSize={iconSize}
            disableRipple={disableRipple}
            elastic={elastic}
            elasticActive={elasticActive}
            style={{
                backgroundColor: isBoolTrue(outlined) ? bgColor : getDefStr(bgColor, getVariant()),
                borderWidth: borderWidth,
                borderColor: isBoolTrue(outlined) ? getDefStr(borderColor, getVariant()) : borderColor,
                color: isBoolTrue(outlined) ? getDefStr(color, getVariant()) : getDefStr(color, theme.textWhite), ...style
            }}
            hoverStyle={{
                backgroundColor: getDefStr(hoverBgColor, getVariantLight()),
                borderColor: isBoolTrue(outlined) ? getDefStr(hoverBorderColor, getDefStr(hoverBgColor, getVariantLight())) : hoverBorderColor,
                color: getDefStr(hoverColor, theme.textWhite),
                ...hoverStyle
            }}
            pressStyle={{
                // backgroundColor: getDefStr(hoverBgColor, getVariantLight()),
                // borderColor: isBoolTrue(outlined) ? getDefStr(hoverBorderColor, getDefStr(hoverBgColor, getVariantLight())) : hoverBorderColor,
                // color: getDefStr(hoverColor, theme.textWhite),
                ...pressStyle
            }}
            touchStyle={{
                backgroundColor: getDefStr(hoverBgColor, getVariantLight()),
                // borderColor: isBoolTrue(outlined) ? getDefStr(hoverBorderColor, getDefStr(hoverBgColor, getVariantLight())) : hoverBorderColor,
                borderColor: isBoolTrue(outlined) ? getDefStr(hoverBorderColor, getDefStr(hoverBgColor, getVariantLight())) : 'transparent',
                color: getDefStr(hoverColor, theme.textWhite),
                ...hoverStyle,
                ...touchStyle,
            }}
            loadingWidth={loadingWidth}
            textOnLoading={textOnLoading}
            loadingColorVariant={loadingVariant}
            enableFocusStyling={enableFocusStyling}
            asLoading={asLoading}
            disableVariant={`disable-item-${disableVariant}`}
            disabled={disabled}
            color={''}
            rippleColor={getDefStr(rippleColor, theme.textWhite)}
            onClick={onClick}
            {...rest}
        />
    }

    function getVariant() {
        return getDefStr(variant, theme.primary);
    }

    function getVariantLight() {
        return getDefStr(variantLight, isBoolTrue(outlined) ? getDefStr(variant, theme.primaryLight) : theme.primaryLight);
    }
});
export default ThemedButton;
